import { useEffect, useImperativeHandle, useMemo, RefObject } from "react";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Seller } from "@api/apiClient/sellers";
import { Alert, AlertType } from "@components/new/Alert";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import TextInputContainer from "@containers/TextInputContainer";
import { validateWbKey, updateWbKey } from "@api/apiClient/sellers";
import { FormHandle } from "./types";
import styles from "./WBForm.module.scss";

export interface WBFormType {
  apiKey: string;
  newApiKey: string;
}

const WBForm = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  seller,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean;
    isLoading?: boolean;
    title?: string;
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  seller: Seller;
}) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<WBFormType>({
    defaultValues: {
      apiKey: seller.wbSeller?.token ? seller.wbSeller?.token.slice(0, 4) + "..." : "",
      newApiKey: "",
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const newApiKey = watch("newApiKey");

  const validationQuery = useQuery({
    queryKey: ["validateWbKey", newApiKey, seller?.wbSeller?.id ?? ""],
    queryFn: () =>
      validateWbKey({ token: newApiKey, wbSellerId: seller?.wbSeller?.id }),
    retry: 1,
    enabled: false,
  });

  const saveStoreMutation = useMutation({
    mutationFn: updateWbKey,
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      if (
        ![
          "verification-ticket-invalid-code",
          "verification-ticket-expired",
          "company-already-linked-to-other-account",
        ].find((v) => v === error?.response?.data?.code)
      ) {
        onError();
      }
    },
  });

  useEffect(() => {
    onStatusChange({
      disabled: !isValid,
      isLoading: Boolean(validationQuery.isLoading || saveStoreMutation.isLoading),
      title: "",
    });
  }, [
    isValid,
    saveStoreMutation.isLoading,
    validationQuery.isLoading,
  ]);

  const onSubmit = (form: WBFormType) => {
    saveStoreMutation.mutate({
      wbSellerId: seller.wbSeller?.id ?? "",
      newToken: form.newApiKey,
    });
  };

  const submit = handleSubmit(onSubmit);

  useImperativeHandle(formRef, () => ({
    submit,
  }));

  const validateApiKey = useMemo(
    () =>
      debounce(async (value, resolve) => {
        const result = await queryClient.fetchQuery({
          queryKey: ["validateWbKey", value, seller?.wbSeller?.id ?? ""],
          queryFn: () =>
            validateWbKey({ token: value, wbSellerId: seller?.wbSeller?.id }),
          staleTime: 5000,
        });

        resolve(
          result?.isSuccess ||
            t(`storesPage.addShopModal.errors.${result.error}`, {
              defaultValue: t("defaultFormErrors.default"),
            }),
        );
      }, 500),
    [],
  );

  return (
    <form className={styles.form} onSubmit={submit}>
      <Alert
        type={AlertType.WARNING}
        title={t('storesPage.addShopModal.apikeyAlertTitle')}
        description={<LinkButton
          to={'https://unitrade-global.com/page139704916.html'}
          title={t('storesPage.addShopModal.apikeyAlertDescription')}
          size={LinkButtonSize.S}
          external
        />}
      />
      <TextInputContainer
        control={control}
        name="apiKey"
        title={t("storesPage.editShopModal.apiKeyInput")}
        disabled
        type="text"
        hasError={Boolean(
          ["Expiring", "Expired", "Invalid"].find(
            (v) => v === seller.wbSeller?.tokenStatus,
          ),
        )}
        error={t(`storesPage.tokenStatus.${seller.wbSeller?.tokenStatus}`)}
      />
      <TextInputContainer
        control={control}
        name="newApiKey"
        title={t("storesPage.editShopModal.newApiKey")}
        placeholder={t("storesPage.editShopModal.newApiKeyPlaceholder")}
        rules={{
          required: t("defaultFormErrors.required"),
          validate: async (value) => {
            if (!value) return true;
            return new Promise((resolve) => {
              validateApiKey(value, resolve);
            });
          },
        }}
        maxLength={999}
      />
    </form>
  );
};

export default WBForm;
